import { Box, Divider, Popover, Search, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useCallback, useEffect, useMemo } from 'react';
import { v4 } from 'uuid';

import { useSiteSelectorBI } from './bi';
import { SEARCH_DEBOUNCE } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { useUserSitesRemoteSearch } from '../../../hooks/useUserSitesRemoteSearch';
import { SiteMetaData } from '../../../types';
import { useStore } from '../../../zustand/stores';
import { SiteSelectorList } from '../SiteSelectorList';
import {
  SiteSelectorState,
  SiteSelectorStateType,
} from '../SiteSelectorStates';

import css from './index.module.scss';

export type SiteSelectorProps = {
  shown: boolean;
  children: JSX.Element;
  onToggle: () => void;
  onClickOption: (site?: SiteMetaData) => void;
  itemId?: string;
  width?: string;
  moveBy?: { x?: number; y?: number };
  moveArrowTo?: number;
};

export const MAX_QUERY_LENGTH = 100;

export const SiteSelectorDropDown = ({
  shown,
  onToggle,
  onClickOption,
  children,
  itemId,
  width,
  moveBy,
  moveArrowTo,
}: SiteSelectorProps) => {
  const siteSelectorData = useStore((state) => state.siteSelectorSlice);
  const { setUserSelectedSite } = siteSelectorData;
  const { selectedSite } = siteSelectorData;
  const {
    query,
    setQuery,
    sites,
    itemsCount: updatedItemsCount,
    loading,
    overlay: errorOrEmptyState,
    loadMore,
    clearSearch,
    retry,
  } = useUserSitesRemoteSearch({ enabled: shown });
  const { t } = useTranslation();
  const bi = useSiteSelectorBI(v4(), itemId);

  const onSearch = (term: string) => {
    setQuery(term);
  };

  const onClearSearch = useCallback(async () => {
    await clearSearch();
  }, [clearSearch]);

  useEffect(() => {
    if (!shown) {
      void clearSearch();
    }
  }, [shown, clearSearch]);

  const textButtonLabel = useMemo(() => {
    if (errorOrEmptyState === SiteSelectorStateType.ERROR) {
      return t('site-selector-modal.refresh');
    }
    if (errorOrEmptyState === SiteSelectorStateType.EMPTY || query.length) {
      return t('site-selector-modal.clear-search');
    }
    return t('site-selector-modal.clear-selection');
  }, [errorOrEmptyState, query.length, t]);

  const onSiteItemClick = (site: SiteMetaData) => {
    const previousSelectedSiteId = selectedSite?.metaSiteId || undefined;
    setUserSelectedSite(site);
    onClickOption(site);
    bi.onClickSiteOrAllSites(previousSelectedSiteId, site.metaSiteId);
  };

  const onClearSelection = () => {
    const previousSelectedSiteId = selectedSite?.metaSiteId || undefined;
    setUserSelectedSite(null);
    onClickOption();
    bi.onClickSiteOrAllSites(previousSelectedSiteId);
  };

  if (!siteSelectorData || !siteSelectorData.sites) {
    return null;
  }

  const isTextButtonDisabled =
    selectedSite === null && !query && !errorOrEmptyState;

  const onClickTextButton = async () => {
    if (errorOrEmptyState === SiteSelectorStateType.ERROR) {
      retry();
    } else if (query.length) {
      await onClearSearch();
    } else {
      onClearSelection();
    }
  };

  return (
    <Popover
      placement="bottom"
      dataHook={DATA_HOOKS.SITE_SELECTOR_POPOVER}
      onClickOutside={onToggle}
      showArrow
      moveArrowTo={moveArrowTo}
      moveBy={moveBy}
      shown={shown}
      width={width}
      appendTo="window"
      animate
    >
      <Popover.Element>{children}</Popover.Element>
      <Popover.Content>
        <Box
          direction="vertical"
          verticalAlign="space-between"
          height="100%"
          boxSizing="border-box"
        >
          <Box
            verticalAlign="middle"
            boxSizing="border-box"
            padding="2 4"
            height="60px"
          >
            <Search
              className={css.siteSelectorSearch}
              size="small"
              placeholder={t('site-selector-modal.search')}
              onClear={onClearSearch}
              dataHook={DATA_HOOKS.SITE_SELECTOR_SEARCH}
              onChange={(e) => onSearch(e.target.value)}
              value={query}
              status={loading ? 'loading' : undefined}
              debounceMs={SEARCH_DEBOUNCE}
              maxLength={MAX_QUERY_LENGTH}
            />
          </Box>
          {errorOrEmptyState ? (
            <SiteSelectorState type={errorOrEmptyState} query={query} />
          ) : (
            <SiteSelectorList
              sites={sites}
              currentSiteId={selectedSite?.metaSiteId}
              onSiteItemClick={onSiteItemClick}
              totalSites={updatedItemsCount}
              fetchMoreSites={loadMore}
            />
          )}
          <Divider />
          <Box verticalAlign="middle" align="center" height="67px">
            <TextButton
              weight="thin"
              size="medium"
              dataHook={DATA_HOOKS.SITE_SELECTOR_TEXT_BUTTON}
              onClick={onClickTextButton}
              disabled={isTextButtonDisabled}
            >
              {textButtonLabel}
            </TextButton>
          </Box>
        </Box>
      </Popover.Content>
    </Popover>
  );
};
